/**
 * i18n helpers for workflow-related labels.
 *
 * Lookup order (key-first, label-second so tenants can translate or
 * override server text without redeploying):
 *
 *   1. ``workflows.{workflowKey}.{kind}.{key}`` (tenant-specific)
 *   2. ``workflows.{kind}.{key}`` (global)
 *   3. server-provided fallback label (English from the seed)
 *   4. the key itself
 *
 * These hooks live in their own file (not co-located with
 * ``WorkflowStateBadge``) so the badge module only exports React
 * components - required by the ``react-refresh/only-export-components``
 * lint rule, and conveniently the same chokepoint can serve timeline /
 * dialog / notification surfaces without re-rendering a badge.
 */

import { useTranslation } from 'react-i18next'

function useWorkflowI18nLabel(
  kind: 'states' | 'actions' | 'workflows',
  key: string | undefined,
  fallbackLabel: string,
  workflowKey?: string,
): string {
  const { t } = useTranslation()
  if (!key) return fallbackLabel || ''
  const scoped = workflowKey
    ? t(`workflows.${workflowKey}.${kind}.${key}`, { defaultValue: '' })
    : ''
  if (scoped) return scoped
  const generic = t(`workflows.${kind}.${key}`, { defaultValue: '' })
  if (generic) return generic
  return fallbackLabel || key
}

export function useWorkflowStateLabel(
  stateKey: string | undefined,
  fallbackLabel: string,
  workflowKey?: string,
): string {
  return useWorkflowI18nLabel('states', stateKey, fallbackLabel, workflowKey)
}

export function useWorkflowActionLabel(
  actionKey: string | undefined,
  fallbackLabel: string,
  workflowKey?: string,
): string {
  return useWorkflowI18nLabel('actions', actionKey, fallbackLabel, workflowKey)
}

export function useWorkflowLabel(
  workflowKey: string | undefined,
  fallbackLabel: string,
): string {
  // For the workflow label itself the scoped lookup *is* the
  // workflow key - there's no separate parent namespace, so we pass
  // ``undefined`` for ``workflowKey`` to skip the redundant scoped
  // tier and read straight from ``workflows.workflows.{key}``.
  return useWorkflowI18nLabel('workflows', workflowKey, fallbackLabel)
}
